/**
 * inputs.ts
 *
 * Client-side validation for Title Analysis inputs.
 *
 * Validates the Use_Case selector and Subject_Loan fields with the same limits
 * as the backend `TitleAnalysisInputs` model (Requirements 7.2, 7.6, 7.7).
 *
 * Requirements: 18.26
 */

/** The five allowed use-case values (Requirement 7.1). */
export const ALLOWED_USE_CASES = [
  'origination',
  'rtl_purchase',
  'npl_acquisition',
  'whole_loan_trade',
  'securitization_diligence',
] as const;

export type UseCase = (typeof ALLOWED_USE_CASES)[number];

export const USE_CASE_LABELS: Record<UseCase, string> = {
  origination: 'Origination',
  rtl_purchase: 'RTL purchase',
  npl_acquisition: 'NPL acquisition',
  whole_loan_trade: 'Whole loan trade',
  securitization_diligence: 'Securitization diligence',
};

/** Maximum lengths for Subject_Loan fields (Requirement 7.2). */
export const SUBJECT_LOAN_LIMITS = {
  vestingEntity: 200,
  guarantorNames: 1000,
  subjectMortgageRecordingReference: 200,
  expectedLienPosition: 50,
  currentLoanHolder: 200,
  apn: 50,
} as const;

/** Minimum and maximum loan amount (Requirement 7.2). */
export const LOAN_AMOUNT_MIN = 0.0;
export const LOAN_AMOUNT_MAX = 999_999_999.99;

/**
 * Validate a use-case value.
 *
 * @param useCase The value to validate.
 * @returns `null` if valid, otherwise an error message.
 *
 * Requirements: 7.6, 7.7, 7.11, 7.12
 */
export function validateUseCase(useCase: string | null | undefined): string | null {
  if (useCase == null || useCase.trim() === '') {
    return 'Use case is required';
  }
  if (!ALLOWED_USE_CASES.includes(useCase as UseCase)) {
    return `Use case must be one of: ${ALLOWED_USE_CASES.join(', ')}`;
  }
  return null;
}

/**
 * Validate vesting entity (max 200 characters).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateVestingEntity(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }
  if (value.length > SUBJECT_LOAN_LIMITS.vestingEntity) {
    return `Vesting entity must be ${SUBJECT_LOAN_LIMITS.vestingEntity} characters or fewer`;
  }
  return null;
}

/**
 * Validate guarantor names (max 1000 characters total).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateGuarantorNames(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }
  if (value.length > SUBJECT_LOAN_LIMITS.guarantorNames) {
    return `Guarantor names must be ${SUBJECT_LOAN_LIMITS.guarantorNames} characters or fewer`;
  }
  return null;
}

/**
 * Validate loan amount (0.00 to 999,999,999.99 with at most 2 decimal places).
 *
 * @param value The value to validate (string or number).
 * @returns `null` if valid or empty, otherwise an error message.
 *
 * Requirements: 7.2, 7.6
 */
export function validateLoanAmount(value: string | number | null | undefined): string | null {
  if (value == null || value === '') {
    return null; // optional field
  }

  const str = String(value).trim();
  if (str === '') {
    return null;
  }

  // Must match pattern: optional digits, optional decimal point, 0-2 digits after decimal
  const decimalRegex = /^\d+(\.\d{0,2})?$/;
  if (!decimalRegex.test(str)) {
    return 'Loan amount must be a number with up to two decimal places';
  }

  const num = parseFloat(str);
  if (isNaN(num)) {
    return 'Loan amount must be a valid number';
  }
  if (num < LOAN_AMOUNT_MIN) {
    return `Loan amount must be at least ${LOAN_AMOUNT_MIN.toFixed(2)}`;
  }
  if (num > LOAN_AMOUNT_MAX) {
    return `Loan amount must be at most ${LOAN_AMOUNT_MAX.toFixed(2)}`;
  }

  // Check decimal places
  const parts = str.split('.');
  if (parts.length === 2 && parts[1].length > 2) {
    return 'Loan amount cannot have more than two decimal places';
  }

  return null;
}

/**
 * Validate note date (ISO 8601 calendar date: YYYY-MM-DD).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 *
 * Requirements: 7.2, 7.6
 */
export function validateNoteDate(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }

  const trimmed = value.trim();
  const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoRegex.test(trimmed)) {
    return 'Note date must be in YYYY-MM-DD format';
  }

  // Ensure the parsed date matches the input (prevents YYYY-MM-DD → invalid month/day)
  const [year, month, day] = trimmed.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return 'Note date must be a valid calendar date';
  }

  return null;
}

/**
 * Validate search start date (ISO 8601 calendar date: YYYY-MM-DD).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateSearchStartDate(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }

  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return 'Search start date must be in YYYY-MM-DD format';
  }

  const [year, month, day] = trimmed.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return 'Search start date must be a valid calendar date';
  }

  return null;
}

/** Indexes a user can mark as searched (mirrored by the backend). */
export const SEARCH_INDEX_OPTIONS: readonly string[] = [
  'Grantor/Grantee Index',
  'Tract/Parcel Index',
  'Judgment and Lien Index',
  'Lis Pendens Index',
  'UCC/Fixture Filing Index',
  'Tax Records',
  'Bankruptcy Records',
  'Probate/Estate Records',
];

/**
 * Validate the selected searched indexes.
 *
 * @param values The selected index names.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateIndexesSearched(values: readonly string[] | null | undefined): string | null {
  if (!values || values.length === 0) {
    return null; // optional field
  }
  if (values.some((value) => !SEARCH_INDEX_OPTIONS.includes(value))) {
    return 'Indexes searched contains an unsupported value';
  }
  return null;
}

/**
 * Validate subject mortgage recording reference (max 200 characters).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateSubjectMortgageRecordingReference(
  value: string | null | undefined
): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }
  if (value.length > SUBJECT_LOAN_LIMITS.subjectMortgageRecordingReference) {
    return `Subject mortgage recording reference must be ${SUBJECT_LOAN_LIMITS.subjectMortgageRecordingReference} characters or fewer`;
  }
  return null;
}

/**
 * Validate expected lien position (max 50 characters).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateExpectedLienPosition(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }
  if (value.length > SUBJECT_LOAN_LIMITS.expectedLienPosition) {
    return `Expected lien position must be ${SUBJECT_LOAN_LIMITS.expectedLienPosition} characters or fewer`;
  }
  return null;
}

/**
 * Validate current loan holder (max 200 characters).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateCurrentLoanHolder(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }
  if (value.length > SUBJECT_LOAN_LIMITS.currentLoanHolder) {
    return `Current loan holder must be ${SUBJECT_LOAN_LIMITS.currentLoanHolder} characters or fewer`;
  }
  return null;
}

/**
 * Validate APN (max 50 characters).
 *
 * @param value The value to validate.
 * @returns `null` if valid or empty, otherwise an error message.
 */
export function validateApn(value: string | null | undefined): string | null {
  if (!value || value.trim() === '') {
    return null; // optional field
  }
  if (value.length > SUBJECT_LOAN_LIMITS.apn) {
    return `APN must be ${SUBJECT_LOAN_LIMITS.apn} characters or fewer`;
  }
  return null;
}

export interface TitleAnalysisInputs {
  useCase?: string | null;
  vestingEntity?: string | null;
  guarantorNames?: string | null;
  loanAmount?: string | number | null;
  noteDate?: string | null;
  searchStartDate?: string | null;
  indexesSearched?: string[] | null;
  subjectMortgageRecordingReference?: string | null;
  expectedLienPosition?: string | null;
  currentLoanHolder?: string | null;
  apn?: string | null;
}

/**
 * Validate all Title Analysis inputs at once.
 *
 * @param inputs Object containing all user-entered fields.
 * @returns Object with the same keys, each containing either `null` (valid) or an error message.
 */
export function validateTitleAnalysisInputs(
  inputs: TitleAnalysisInputs
): Record<keyof TitleAnalysisInputs, string | null> {
  return {
    useCase: inputs.useCase ? validateUseCase(inputs.useCase) : null,
    vestingEntity: validateVestingEntity(inputs.vestingEntity),
    guarantorNames: validateGuarantorNames(inputs.guarantorNames),
    loanAmount: validateLoanAmount(inputs.loanAmount),
    noteDate: validateNoteDate(inputs.noteDate),
    searchStartDate: validateSearchStartDate(inputs.searchStartDate),
    indexesSearched: validateIndexesSearched(inputs.indexesSearched),
    subjectMortgageRecordingReference: validateSubjectMortgageRecordingReference(
      inputs.subjectMortgageRecordingReference
    ),
    expectedLienPosition: validateExpectedLienPosition(inputs.expectedLienPosition),
    currentLoanHolder: validateCurrentLoanHolder(inputs.currentLoanHolder),
    apn: validateApn(inputs.apn),
  };
}
